import { Box, Button, Modal, Popover, Select, Skeleton, Table, Text, Tooltip, Flex } from "@mantine/core";
import { ArrowClockwiseIcon, LightningIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import qs from "qs";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IInboxEvent, IInboxEventDetail, IInboxResponse } from "../../apis/ledger";
import { Page, PageHeader } from "../../components/layout/Page";
import { DateRangeChip, ListFooter, ListToolbar, type ToolbarTab } from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { usePermission } from "../../components/permission";
import { Notice } from "../../components/ui/notice";
import { message } from "../../utils/Message";
import { EventDrawer, useAggregateName, useEventName } from "./EventDrawer";
import { INBOX_EVENT_TYPES, INBOX_STATUSES, STATUS_PILL, firstLine } from "./events-shared";
import eventClasses from "./events.module.css";
import { useTemplateLabel } from "./journal-shared";
import classes from "./journal.module.css";
import { ListStates, resolveListStates } from "../../components/list";

/**
 * Үйл явдал → дэвтэр (SPRINT-04 §8).
 *
 *   • «Алгасав» нь АЛДАА БИШ — тухайн үйл явдалд дансны бичилт хийх дүрэм байхгүй.
 *   • Өртөг тодорхойгүй бол бичилт ХИЙГДЭХГҮЙ (0-ээр бичихгүй) — мөр хүлээлгэнд
 *     орж эндээс харагдана.
 */

const COLUMN_COUNT = 7;
const SKELETON_ROWS = 8;
const LIMIT = 50;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

type EventsQuery = {
  tab: string;
  from: string | null;
  to: string | null;
  type: string | null;
  page: number;
};

function readQuery(params: URLSearchParams): EventsQuery {
  const tab = params.get("tab") ?? "";
  const from = params.get("from");
  const to = params.get("to");
  const type = params.get("type");
  return {
    tab: (INBOX_STATUSES as string[]).includes(tab) ? tab : "",
    from: from && YMD.test(from) ? from : null,
    to: to && YMD.test(to) ? to : null,
    type: type && INBOX_EVENT_TYPES.includes(type) ? type : null,
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
  };
}

function writeQuery(q: EventsQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.tab) p.set("tab", q.tab);
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.type) p.set("type", q.type);
  if (q.page !== 1) p.set("page", String(q.page));
  return p;
}

export const LedgerEventsPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const eventName = useEventName();
  const aggregateName = useAggregateName();
  const templateLabel = useTemplateLabel();
  const canReplay = usePermission("INV_SETT_SEND").hasAccess;
  const [searchParams, setSearchParams] = useSearchParams();
  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<EventsQuery>, resetPage = true) =>
      setSearchParams(writeQuery({ ...q, ...patch, ...(resetPage ? { page: 1 } : {}) }), { replace: true }),
    [q, setSearchParams],
  );

  const [openId, setOpenId] = useState<string | null>(null);
  const [replayTarget, setReplayTarget] = useState<IInboxEventDetail | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [busy, setBusy] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);

  const params = {
    page: q.page,
    limit: LIMIT,
    ...(q.from ? { dateFrom: q.from } : {}),
    ...(q.to ? { dateTo: q.to } : {}),
    ...(q.type ? { eventType: q.type } : {}),
    ...(q.tab ? { status: q.tab } : {}),
  };

  const { data, error, isLoading, isValidating, mutate } = useSwr<IInboxResponse>(
    `ledger.inbox?${qs.stringify(params)}`,
    async () => await LedgerApi.getInboxEvents(params),
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const events = useMemo(() => data?.events ?? [], [data]);
  const counts = data?.counts;
  const total = data?.pagination.total ?? 0;
  const showError = !!error && !isValidating;
  const showSkeleton = isLoading || (isValidating && !data && !error);
  const dash = t("common:list.dash");
  const hasFilters = !!(q.tab || q.from || q.to || q.type);
  const ready = !!counts && !showError;

  const allCount = counts ? counts.posted + counts.received + counts.pendingCost + counts.failed + counts.skipped : undefined;
  const countOf = (status: string) =>
    !counts ? undefined : { POSTED: counts.posted, RECEIVED: counts.received, PENDING_COST: counts.pendingCost, FAILED: counts.failed, SKIPPED: counts.skipped }[status];
  const tabs: ToolbarTab[] = [
    { value: "", label: t("billing:ledger.jrn.tabs.all"), count: allCount },
    ...INBOX_STATUSES.map((s) => ({
      value: s,
      label: t(`billing:ledger.inbox.status.${s}`),
      count: countOf(s),
      tone: s === "FAILED" && (counts?.failed ?? 0) > 0 ? ("danger" as const) : undefined,
    })),
  ];

  const groups = useMemo(() => {
    const out: { date: string; items: IInboxEvent[] }[] = [];
    for (const e of events) {
      const date = dayjs(e.occurredAt).format("YYYY-MM-DD");
      const last = out[out.length - 1];
      if (last && last.date === date) last.items.push(e);
      else out.push({ date, items: [e] });
    }
    return out;
  }, [events]);
  const orderedIds = useMemo(() => events.map((e) => e.id), [events]);

  const clearFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  const replay = async () => {
    if (!replayTarget) return;
    setBusy(true);
    try {
      const result = await LedgerApi.replayInboxEvent(replayTarget.id);
      message.success(t(`billing:ledger.inbox.replayed.${result.status}`, { defaultValue: t("billing:ledger.inbox.replayedGeneric") }));
      setReplayTarget(null);
      setRefreshToken((n) => n + 1);
      await mutate();
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const overdue = counts?.overdueCost ?? 0;
  const failed = counts?.failed ?? 0;
  const showWarn = ready && (failed > 0 || overdue > 0);

  const chipClass = (active: boolean) => `${sharedListClasses.filterChip}${active ? ` ${sharedListClasses.filterChipActive}` : ""}`;

  const kpis = [
    { key: "POSTED", dot: eventClasses.dotPosted, value: counts?.posted },
    { key: "RECEIVED", dot: eventClasses.dotReceived, value: counts?.received },
    { key: "PENDING_COST", dot: eventClasses.dotPending, value: counts?.pendingCost, hint: overdue > 0 ? t("billing:ledger.inbox.overdueCost", { count: overdue }) : null },
    { key: "FAILED", dot: eventClasses.dotFailed, value: counts?.failed, danger: failed > 0 },
    { key: "SKIPPED", dot: eventClasses.dotSkipped, value: counts?.skipped },
  ];

  const renderRow = (e: IInboxEvent) => {
    const errText = e.status === "FAILED" ? firstLine(e.lastError) : "";
    return (
      <Table.Tr
        key={e.id}
        className={`${classes.row} ${e.id === openId ? classes.rowActive : ""}`}
        tabIndex={0}
        role="button"
        aria-label={eventName(e.eventType)}
        onClick={() => setOpenId(e.id)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            setOpenId(e.id);
          }
        }}
      >
        <Table.Td>{dayjs(e.occurredAt).format("HH:mm")}</Table.Td>
        <Table.Td>
          <Box className={classes.stack}>
            <Text span className={classes.desc}>{eventName(e.eventType)}</Text>
            <Text span className={classes.sub}>
              {[aggregateName(e.aggregateType), e.sourceRef].filter(Boolean).join(" · ")}
            </Text>
          </Box>
        </Table.Td>
        <Table.Td>
          <Text span size="sm" className={e.templateCode ? undefined : eventClasses.muted}>
            {templateLabel(e.templateCode) ?? dash}
          </Text>
        </Table.Td>
        <Table.Td>
          <Text span className={`${classes.pill} ${eventClasses[STATUS_PILL[e.status]]}`}>
            {t(`billing:ledger.inbox.status.${e.status}`)}
          </Text>
        </Table.Td>
        <Table.Td>
          {e.status === "POSTED" && e.entryId ? (
            <Link
              className={eventClasses.entryLink}
              to={`/ledger/journal?entry=${e.entryId}`}
              onClick={(ev) => ev.stopPropagation()}
            >
              {e.entryNumber ?? t("billing:ledger.inbox.openEntry")}
            </Link>
          ) : (
            <Text span className={eventClasses.muted}>{dash}</Text>
          )}
        </Table.Td>
        <Table.Td>
          {e.retryCount > 0 ? e.retryCount : <Text span className={eventClasses.muted}>{dash}</Text>}
        </Table.Td>
        <Table.Td>
          {errText ? (
            <Tooltip label={e.lastError} multiline maw={420} withArrow>
              <Text span className={eventClasses.errorCell}>{errText}</Text>
            </Tooltip>
          ) : (
            <Text span className={eventClasses.muted}>{dash}</Text>
          )}
        </Table.Td>
      </Table.Tr>
    );
  };

  const listStates = resolveListStates(
    <ListStates
      loading={showSkeleton}
      error={showError ? error : undefined}
      onRetry={() => mutate()}
      empty={showError || showSkeleton || events.length === 0}
      filtered={hasFilters}
      onClear={clearFilters}
      columns={COLUMN_COUNT}
      emptyTitle={t("billing:ledger.inbox.emptyTitle")}
      emptyDescription={t("billing:ledger.inbox.emptyBody")}
      emptyAction={<Link className={eventClasses.emptyLink} to="/ledger/journal">{t("billing:ledger.inbox.emptyAction")}</Link>}
    />
  );

  const renderBody = () => {
    return groups.map((g) => (
      <Fragment key={g.date}>
        <Table.Tr className={classes.groupRow}>
          <Table.Td colSpan={COLUMN_COUNT}>
            {t("billing:ledger.inbox.group", { date: g.date, count: g.items.length })}
          </Table.Td>
        </Table.Tr>
        {g.items.map(renderRow)}
      </Fragment>
    ));
  };

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("billing:ledger.inbox.title")}
            {ready && total > 0 && <Text span className={classes.titleChip}>{allCount}</Text>}
          </>
        }
        subtitle={t("billing:ledger.inbox.subtitle")}
      />

      {showWarn && (
        <Notice
          tone={failed > 0 ? "danger" : "warning"}
          title={t("billing:ledger.inbox.warn", { count: failed + overdue })}
          action={
            <Button variant="default" size="xs" onClick={() => patchQuery({ tab: failed > 0 ? "FAILED" : "PENDING_COST" })}>
              {failed > 0 ? t("billing:ledger.inbox.warnFailed") : t("billing:ledger.inbox.warnPending")}
            </Button>
          }
        />
      )}

      <Box className={eventClasses.kpi} aria-label={t("billing:ledger.inbox.title")}>
        {kpis.map((k) => (
          <Box key={k.key} className={eventClasses.kpiCell}>
            <Text span className={eventClasses.kpiLabel}>
              <Box component="span" className={`${eventClasses.dot} ${k.dot}`} />
              {t(`billing:ledger.inbox.status.${k.key}`)}
            </Text>
            <Text span className={`${eventClasses.kpiValue} ${k.danger ? eventClasses.kpiValueDanger : ""}`}>
              {ready ? k.value : dash}
            </Text>
            {k.hint && <Text span className={eventClasses.kpiHint}>{k.hint}</Text>}
          </Box>
        ))}
      </Box>

      <Box className={classes.card}>
        <ListToolbar
          tabs={tabs}
          activeTab={q.tab}
          onTabChange={(tab) => patchQuery({ tab })}
          filterSlot={
            <>
              <DateRangeChip
                label={t("billing:ledger.jrn.chipDate")}
                value={[q.from, q.to]}
                onChange={([from, to]) =>
                  patchQuery({
                    from: from ? dayjs(from).format("YYYY-MM-DD") : null,
                    to: to ? dayjs(to).format("YYYY-MM-DD") : null,
                  })
                }
              />
              <Popover opened={typeOpen} onChange={setTypeOpen} position="bottom-end" shadow="md" withinPortal>
                <Popover.Target>
                  <Box component="button" type="button" className={chipClass(!!q.type)} onClick={() => setTypeOpen((o) => !o)}>
                    {q.type ? (
                      <>
                        <Text span size="sm" fw={500}>{eventName(q.type)}</Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            patchQuery({ type: null });
                            setTypeOpen(false);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">{t("billing:ledger.inbox.chipType")}</Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Select
                    clearable
                    comboboxProps={{ withinPortal: false }}
                    data={INBOX_EVENT_TYPES.map((v) => ({ value: v, label: eventName(v) }))}
                    value={q.type}
                    onChange={(v) => {
                      patchQuery({ type: v });
                      setTypeOpen(false);
                    }}
                    placeholder={t("billing:ledger.inbox.typePick")}
                    w={280}
                  />
                </Popover.Dropdown>
              </Popover>
            </>
          }
        />

        <Box className={classes.wrap}>
          {listStates ?? (
          <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("billing:ledger.inbox.colTime")}</Table.Th>
                <Table.Th>{t("billing:ledger.inbox.colEvent")}</Table.Th>
                <Table.Th>{t("billing:ledger.inbox.colTemplate")}</Table.Th>
                <Table.Th>{t("billing:ledger.colStatus")}</Table.Th>
                <Table.Th>{t("billing:ledger.inbox.colEntry")}</Table.Th>
                <Table.Th>{t("billing:ledger.inbox.colAttempts")}</Table.Th>
                <Table.Th>{t("billing:ledger.inbox.colError")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{renderBody()}</Table.Tbody>
          </Table>
          )}
        </Box>

        {!showError && total > 0 && (
          <ListFooter
            from={Math.min(total, (q.page - 1) * LIMIT + 1)}
            to={Math.min(total, (q.page - 1) * LIMIT + events.length)}
            total={total}
            page={q.page}
            pageSize={LIMIT}
            onPageChange={(p) => patchQuery({ page: p }, false)}
            onPageSizeChange={() => undefined}
            pageSizeOptions={[String(LIMIT)]}
          />
        )}
      </Box>

      <EventDrawer
        eventId={openId}
        orderedIds={orderedIds}
        canReplay={canReplay}
        suspendKeys={replayTarget !== null}
        refreshToken={refreshToken}
        onClose={() => setOpenId(null)}
        onNavigate={setOpenId}
        onRequestReplay={setReplayTarget}
      />

      <Modal
        opened={replayTarget !== null}
        onClose={() => setReplayTarget(null)}
        title={t("billing:ledger.inbox.replayConfirmTitle")}
        size="sm"
      >
        <Text size="sm" mb="md">{t("billing:ledger.inbox.replayConfirmBody")}</Text>
        <Flex justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => setReplayTarget(null)} disabled={busy}>{t("common:cancel")}</Button>
          <Button loading={busy} leftSection={<ArrowClockwiseIcon size={14} />} onClick={replay}>
            {t("billing:ledger.inbox.replay")}
          </Button>
        </Flex>
      </Modal>
    </Page>
  );
};

export default LedgerEventsPage;
